import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { shallow } from 'zustand/shallow'
import { Avatar, Button, FormGroup, Input } from '@/design-system'
import useAppStore from '@/store/useAppStore'
import {
  useGetBasicInfo,
  useRemoveCoverPicture,
  useUpdateProfile,
  useUploadCoverPicture,
  useUploadProfilePicture,
} from '@/hooks/api/dashboard'
import { useAuth } from '@/hooks/common'
import { SettingsField, fieldInputSx, fieldTextareaSx } from '../SettingsField'
import toast from 'react-hot-toast'
import { fireAlertToast } from '@/design-system/alerts'

const PanelHead = styled(Box)({ marginBottom: 18 })
const H2 = styled(Typography)({ fontSize: 20, fontWeight: 720, letterSpacing: '-0.025em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13, color: 'var(--ds-ink-subtle)', marginTop: 4 })

const Hint = styled('div')({ fontSize: 11.5, color: 'var(--ds-ink-subtle)', marginTop: 10 })

const Cover = styled(Box)({
  position: 'relative',
  height: 170,
  borderRadius: 'var(--ds-radius-md)',
  overflow: 'hidden',
  background: 'var(--ds-gradient-spectrum-soft, var(--ds-hover))',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  '&:hover .cover-veil': { opacity: 1 },
})

const CoverVeil = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  opacity: 0,
  background: 'rgba(13,13,15,0.45)',
  transition: `opacity var(--ds-duration-normal) var(--ds-ease)`,
})

const AvatarRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  padding: '22px 0',
  borderBottom: '1px solid var(--ds-border)',
  marginBottom: 22,
})

const AvWrap = styled(Box)({ position: 'relative', flexShrink: 0 })

const AvBadge = styled('button')({
  all: 'unset',
  position: 'absolute',
  bottom: -2,
  right: -2,
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: 'var(--ds-ink)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  boxShadow: '0 0 0 3px var(--ds-canvas)',
  cursor: 'pointer',
  transition: `transform var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover': { transform: 'scale(1.08)' },
})

const NameRow = styled(Box)({ display: 'flex', gap: 12 })

const ReadOnly = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  height: 44,
  padding: '0 14px',
  borderRadius: 'var(--ds-radius-md)',
  background: 'var(--ds-hover)',
  fontSize: 14,
  color: 'var(--ds-ink)',
})

const Verified = styled('span')({
  marginLeft: 'auto',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  padding: '3px 8px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'var(--ds-gradient-spectrum-soft, var(--ds-hover))',
  color: 'var(--ds-accent-deep-b, var(--ds-ink))',
  fontWeight: 600,
})

const CharCount = styled('div')({
  textAlign: 'right',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  color: 'var(--ds-ink-subtle)',
  marginTop: 6,
})

const BIO_MAX = 160

/** Profile identity — cover, avatar, name, bio, email. Wired to `EgoBasicInfo` + the legacy accSettUpdate/upload endpoints. */
export function ProfileSection() {
  const { user } = useAuth()
  const [profile] = useAppStore((state) => [state.user.profile], shallow)
  const getBasicInfo = useGetBasicInfo(user?.userID ?? '')
  const updateProfile = useUpdateProfile()
  const uploadAvatar = useUploadProfilePicture()
  const uploadCover = useUploadCoverPicture()
  const removeCover = useRemoveCoverPicture()

  // `/settings` sits outside the legacy layout that normally kicks this fetch off on
  // mount (Header/ResponsiveNavBar), so without this the fields below render blank.
  useEffect(() => {
    if (user?.userID) getBasicInfo.mutate({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userID])

  const avatarInputRef = useRef<HTMLInputElement>(null)
  const coverInputRef = useRef<HTMLInputElement>(null)

  const [firstName, setFirstName] = useState(profile?.firstName ?? '')
  const [lastName, setLastName] = useState(profile?.lastName ?? '')
  const [bio, setBio] = useState(profile?.motto ?? '')

  useEffect(() => {
    setFirstName(profile?.firstName ?? '')
    setLastName(profile?.lastName ?? '')
    setBio(profile?.motto ?? '')
  }, [profile?.firstName, profile?.lastName, profile?.motto])

  const dirty = firstName !== (profile?.firstName ?? '') || lastName !== (profile?.lastName ?? '') || bio !== (profile?.motto ?? '')

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({ firstName, lastName, motto: bio })
      fireAlertToast('settings.profileUpdated')
    } catch {
      fireAlertToast('settings.profileUpdateFailed')
    }
  }

  // Neither upload mutation updates the store itself — it only tells the backend
  // about the new file. Re-fetching basic info afterwards is what actually pulls the
  // new URLs back down so the cover/avatar shown here refreshes instead of looking
  // like the upload silently did nothing.
  const handleAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    toast.promise(
      uploadAvatar.mutateAsync({ file }).then(() => getBasicInfo.mutateAsync({})),
      { loading: 'Uploading photo…', success: 'Profile picture updated', error: 'Upload failed' },
    )
  }

  const handleCoverPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    toast.promise(
      uploadCover.mutateAsync({ file }).then(() => getBasicInfo.mutateAsync({})),
      { loading: 'Uploading cover…', success: 'Cover photo updated', error: 'Upload failed' },
    )
  }

  const handleRemoveCover = () => {
    toast.promise(
      removeCover.mutateAsync().then(() => getBasicInfo.mutateAsync({})),
      { loading: 'Removing cover…', success: 'Cover photo removed', error: 'Could not remove cover photo' },
    )
  }

  const initial = (profile?.firstName || profile?.userName || '?').charAt(0).toUpperCase()

  return (
    <Box>
      <PanelHead>
        <H2>Profile</H2>
        <Sub>How you appear to collaborators and guests.</Sub>
      </PanelHead>

      <FormGroup label='Cover photo'>
        <input ref={coverInputRef} type='file' accept='image/*,.heic,.heif' hidden onChange={handleCoverPick} />
        <Cover style={{ backgroundImage: profile?.profileCoverURL ? `url(${profile.profileCoverURL})` : undefined }}>
          <CoverVeil className='cover-veil'>
            <Button variant='night' size='sm' onClick={() => coverInputRef.current?.click()} loading={uploadCover.isLoading}>
              ⤒ Upload
            </Button>
            {profile?.profileCoverURL && (
              <Button variant='night' size='sm' onClick={handleRemoveCover} loading={removeCover.isLoading}>
                ✕ Remove
              </Button>
            )}
          </CoverVeil>
        </Cover>
        <Hint>Recommended 1500×500px · max 5MB</Hint>
      </FormGroup>

      <FormGroup>
        <input ref={avatarInputRef} type='file' accept='image/*,.heic,.heif' hidden onChange={handleAvatarPick} />
        <AvatarRow>
          <AvWrap>
            <Avatar
              src={profile?.profilePicThumbURL}
              sx={{
                width: 84,
                height: 84,
                fontSize: 26,
                fontWeight: 700,
                background: 'var(--ds-gradient-spectrum, var(--ds-ink))',
                color: '#fff',
                boxShadow: '0 0 0 3px var(--ds-canvas), var(--ds-shadow-soft)',
              }}
            >
              {initial}
            </Avatar>
            <AvBadge onClick={() => avatarInputRef.current?.click()} aria-label='Change photo'>
              ✎
            </AvBadge>
          </AvWrap>
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 650, color: 'var(--ds-ink)' }}>Profile picture</Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--ds-ink-subtle)', margin: '4px 0 10px', maxWidth: 320, lineHeight: 1.5 }}>
              Square images work best. This is what collaborators and guests see next to your comments, pins, and presence.
            </Typography>
            <Button variant='secondary' size='sm' onClick={() => avatarInputRef.current?.click()} loading={uploadAvatar.isLoading}>
              Change photo
            </Button>
          </Box>
        </AvatarRow>

        <SettingsField label='Display name' hint='Your public name'>
          <NameRow>
            <Input variant='filled' fullWidth sx={{ ...fieldInputSx, flex: 1, minWidth: 0 }} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder='First name' />
            <Input variant='filled' fullWidth sx={{ ...fieldInputSx, flex: 1, minWidth: 0 }} value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder='Last name' />
          </NameRow>
        </SettingsField>

        <SettingsField label='Bio' hint='Shown on your profile'>
          <Input
            variant='filled'
            fullWidth
            multiline
            minRows={3}
            sx={fieldTextareaSx}
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX))}
            placeholder='Write a short bio…'
          />
          <CharCount>{bio.length} / {BIO_MAX}</CharCount>
        </SettingsField>

        <SettingsField label='Email' hint='Your account email'>
          <ReadOnly>
            {profile?.userEmail || '—'}
            <Verified>VERIFIED</Verified>
          </ReadOnly>
        </SettingsField>

        <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant='primary' size='sm' disabled={!dirty} loading={updateProfile.isLoading} onClick={handleSave}>
            Save changes
          </Button>
        </Box>
      </FormGroup>
    </Box>
  )
}
